/**
 * Compliance CRUD uses {@link useComplianceHook} → `GET|POST /v1/settings/compliance`,
 * `PUT|DELETE /v1/settings/compliance/{id}` per OpenAPI (`SettingsController_*Compliance*`).
 */
import { FC, useState, useMemo, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { ComplianceViewProps } from "./compliance.interfaces";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VerifiedUser as VerifiedUserIcon,
  CheckCircle,
  Cancel,
  WarningAmber as WarningAmberIcon,
  HourglassEmpty,
  Autorenew,
  EventBusy,
  RateReview,
  ShieldOutlined,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import {
  useComplianceHook,
  useDeleteComplianceHook,
  useComplianceTemplatesHook,
} from "../../../hooks/useComplianceHook";
import {
  TCompliance,
  TComplianceStatus,
  TComplianceCategory,
  COMPLIANCE_STATUSES,
  COMPLIANCE_STATUS_LABELS,
} from "../../../apis/compliance/compliance.interfaces";
import { useInstituteContext } from "../../../context/institute.context";
import dayjs from "dayjs";
import ManageComplianceDialog from "./manageComplianceDialog";
import TemplateSelectionDialog from "./templateSelectionDialog";
import { logger } from "../../../utils/common";
import { Description as DescriptionIcon } from "@mui/icons-material";

const COMPLIANCE_STATUS_FILTER_OPTIONS = [
  "all",
  ...COMPLIANCE_STATUSES,
] as const;

type ComplianceStatusFilter =
  (typeof COMPLIANCE_STATUS_FILTER_OPTIONS)[number];

const ComplianceView: FC<ComplianceViewProps> = () => {
  const { selectedInstitute } = useInstituteContext();
  const { data, isLoading, error } = useComplianceHook();
  const { data: templates, isLoading: templatesLoading } =
    useComplianceTemplatesHook();
  const deleteComplianceMutation = useDeleteComplianceHook();
  const { control, watch } = useForm({
    defaultValues: { search: "" },
  });
  const searchQuery = watch("search");
  const [categoryFilter, setCategoryFilter] = useState<
    TComplianceCategory | "all"
  >("all");
  const [statusFilter, setStatusFilter] =
    useState<ComplianceStatusFilter>("all");
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [editingCompliance, setEditingCompliance] =
    useState<TCompliance | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [complianceToDelete, setComplianceToDelete] =
    useState<TCompliance | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const filteredCompliances = useMemo(() => {
    if (!data) return [];

    return data.filter((compliance) => {
      const matchesSearch =
        compliance.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (compliance.description
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ??
          false);

      const matchesCategory =
        categoryFilter === "all" || compliance.category === categoryFilter;
      const matchesStatus =
        statusFilter === "all" || compliance.status === statusFilter;

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [data, searchQuery, categoryFilter, statusFilter]);

  const getStatusChip = (status: TComplianceStatus) => {
    const statusVisual: Record<
      TComplianceStatus,
      {
        color: "success" | "error" | "warning" | "info" | "default";
        icon: ReactElement;
      }
    > = {
      COMPLIANT: {
        color: "success",
        icon: <CheckCircle fontSize="small" />,
      },
      NON_COMPLIANT: {
        color: "error",
        icon: <Cancel fontSize="small" />,
      },
      PARTIALLY_COMPLIANT: {
        color: "warning",
        icon: <WarningAmberIcon fontSize="small" />,
      },
      PENDING: {
        color: "default",
        icon: <HourglassEmpty fontSize="small" />,
      },
      IN_PROGRESS: {
        color: "info",
        icon: <Autorenew fontSize="small" />,
      },
      EXPIRED: {
        color: "warning",
        icon: <EventBusy fontSize="small" />,
      },
      EXEMPT: {
        color: "default",
        icon: <ShieldOutlined fontSize="small" />,
      },
      UNDER_REVIEW: {
        color: "info",
        icon: <RateReview fontSize="small" />,
      },
    };

    const visual = statusVisual[status];
    return (
      <Chip
        label={COMPLIANCE_STATUS_LABELS[status]}
        color={visual.color}
        size="small"
        icon={visual.icon}
      />
    );
  };

  const getCategoryChip = (category: TComplianceCategory) => {
    const categoryLabels: Record<TComplianceCategory, string> = {
      regulatory: "Regulatory",
      institutional: "Institutional",
      accreditation: "Accreditation",
      other: "Other",
    };

    return (
      <Chip label={categoryLabels[category]} variant="outlined" size="small" />
    );
  };

  const formatDueDate = (dueDate?: string) => {
    if (!dueDate) return "N/A";
    return dayjs(dueDate).format("MMM DD, YYYY");
  };

  const handleAddCompliance = () => {
    setEditingCompliance(null);
    setManageDialogOpen(true);
  };

  const handleEditCompliance = (compliance: TCompliance) => {
    setEditingCompliance(compliance);
    setManageDialogOpen(true);
  };

  const handleDeleteCompliance = (compliance: TCompliance) => {
    setComplianceToDelete(compliance);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!complianceToDelete) return;

    try {
      await deleteComplianceMutation.mutateAsync(complianceToDelete.id);
      logger.show_success("Compliance deleted successfully!");
      setDeleteDialogOpen(false);
      setComplianceToDelete(null);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to delete compliance. Please try again.",
      );
    }
  };

  const handleManageDialogClose = () => {
    setManageDialogOpen(false);
    setEditingCompliance(null);
  };

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.compliance} />
        <Box p={3} data-testid="compliance-view">
          <MainTitle title="Compliance" />
          <Alert severity="info">
            Please select an institute from the dashboard to view compliances.
          </Alert>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.compliance} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="compliance-view"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.compliance} />
        <Box p={3} data-testid="compliance-view">
          <MainTitle title="Compliance" />
          <Alert severity="error">
            Failed to load compliances. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.compliance} />

      <Grid container spacing={3} data-testid="compliance-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="compliance-header"
          >
            <MainTitle title="Compliance" />
            <Box
              display="flex"
              gap={2}
              data-testid="compliance-actions-container"
            >
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={() => setTemplateDialogOpen(true)}
                size="large"
                data-testid="apply-template-button"
              >
                Apply Template
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddCompliance}
                size="large"
                data-testid="add-compliance-button"
              >
                Add Compliance
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Search and Filters */}
        <Grid item xs={12}>
          <Card data-testid="compliance-filters-card">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ControlledTextField
                    control={control}
                    name="search"
                    id="compliance-search"
                    label="Search"
                    placeholder="Search by name or description..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    isGridItem={false}
                    data-testid="compliance-search-input"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Typography
                      variant="body2"
                      sx={{ alignSelf: "center", minWidth: "70px" }}
                    >
                      Category:
                    </Typography>
                    {(
                      [
                        "all",
                        "regulatory",
                        "institutional",
                        "accreditation",
                        "other",
                      ] as const
                    ).map((category) => (
                      <Chip
                        key={category}
                        label={
                          category === "all"
                            ? "All"
                            : category.charAt(0).toUpperCase() +
                              category.slice(1)
                        }
                        onClick={() => setCategoryFilter(category)}
                        color={
                          categoryFilter === category ? "primary" : "default"
                        }
                        variant={
                          categoryFilter === category ? "filled" : "outlined"
                        }
                        size="small"
                        data-testid={`compliance-category-filter-${category}`}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Typography
                      variant="body2"
                      sx={{ alignSelf: "center", minWidth: "60px" }}
                    >
                      Status:
                    </Typography>
                    {COMPLIANCE_STATUS_FILTER_OPTIONS.map((status) => (
                      <Chip
                        key={status}
                        label={
                          status === "all"
                            ? "All"
                            : COMPLIANCE_STATUS_LABELS[status]
                        }
                        onClick={() => setStatusFilter(status)}
                        color={statusFilter === status ? "primary" : "default"}
                        variant={
                          statusFilter === status ? "filled" : "outlined"
                        }
                        size="small"
                        data-testid={`compliance-status-filter-${status}`}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Compliance List */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Compliances ({filteredCompliances.length})
          </Typography>
        </Grid>

        {filteredCompliances.length === 0 ? (
          <Grid item xs={12}>
            <Card data-testid="compliance-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                >
                  <VerifiedUserIcon
                    sx={{ fontSize: 64, color: "text.secondary" }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    {data?.length === 0
                      ? "No compliances configured"
                      : "No compliances match your search criteria"}
                  </Typography>
                  {data?.length === 0 && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Get started by adding your first compliance requirement.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddCompliance}
                        data-testid="add-compliance-empty-button"
                      >
                        Add Compliance
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <TableContainer
              component={Paper}
              data-testid="compliance-table-container"
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Frequency</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCompliances.map((compliance) => (
                    <TableRow
                      key={compliance.id}
                      hover
                      data-testid={`compliance-row-${compliance.id}`}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {compliance.name}
                          </Typography>
                          {compliance.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {compliance.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getCategoryChip(compliance.category)}
                      </TableCell>
                      <TableCell>{getStatusChip(compliance.status)}</TableCell>
                      <TableCell>{formatDueDate(compliance.dueDate)}</TableCell>
                      <TableCell>
                        {compliance.frequency ? (
                          <Chip
                            label={
                              compliance.frequency.charAt(0).toUpperCase() +
                              compliance.frequency.slice(1)
                            }
                            variant="outlined"
                            size="small"
                          />
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEditCompliance(compliance)}
                          color="primary"
                          data-testid={`edit-compliance-${compliance.id}`}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCompliance(compliance)}
                          color="error"
                          data-testid={`delete-compliance-${compliance.id}`}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>

      <ManageComplianceDialog
        open={manageDialogOpen}
        onClose={handleManageDialogClose}
        compliance={editingCompliance}
      />

      <TemplateSelectionDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        templates={templates || []}
        isLoading={templatesLoading}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setComplianceToDelete(null);
        }}
        data-testid="delete-compliance-dialog"
      >
        <DialogTitle>Delete Compliance</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete compliance "
            {complianceToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setComplianceToDelete(null);
            }}
            disabled={deleteComplianceMutation.isPending}
            data-testid="cancel-delete-compliance-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteComplianceMutation.isPending}
            startIcon={
              deleteComplianceMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-delete-compliance-button"
          >
            {deleteComplianceMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ComplianceView;
